import React, { createContext, useCallback, useContext, useState } from 'react'
import { useSelector } from 'react-redux'

import { RootState } from 'appReducer'
import { useAppDispatch } from 'util/useAppDispatch'

import {
    fetchLocationConnectPaymentInfoData,
    fetchLocationStripeAccountId,
    loadLocationPaymentsInfo,
} from '../../actions'
import { getLocationPaymentsSettings, openEditSurveyModal } from '../actions'
import { openLocationAddEditModal } from '../location-forms/actions'
import { openPaymentRatesModal } from '../payment-rates/actions'
import { openStatementDescriptorModal } from '../statement-descriptor/actions'
import { openStripeAccountIdModal } from '../stripe-account-id/actions'
import { openTerminalModal } from '../terminal-form/actions'
import { AddTerminalRadio } from '../terminal-form/TerminalForm'

interface ExpandedPaymentsState {
    id: string
    isLoading: boolean
    errorMessage: string
}

interface LocationCardContextValue {
    practiceId: string
    hasPayments: boolean
    hasDirectScheduling: boolean
    isLocationEditable: boolean
    canManageSurvey: boolean
    expandedPayments: ExpandedPaymentsState | null
    openEditLocation: (location: Models.PracticeLocation) => Promise<void>
    openEditSurvey: (location: Models.PracticeLocation) => void
    openLocationProducts: (locationId: string) => void
    togglePaymentsExpand: (locationId?: string) => void
    openTerminal: (
        locationId: string,
        terminalId: string,
        nickname: string,
        readerSerial: string,
        pairingCode: string | undefined,
        s700Code: string | undefined,
        uuid: string | undefined,
        addTerminalRadio: AddTerminalRadio,
        isView: boolean,
    ) => void
    openStripeAccountId: (
        locationId: string,
        locationName: string,
        stripeAccountId: string,
        applyToAll: boolean,
    ) => void
    openPaymentsDescriptor: (
        locationId: string,
        statementDescriptor: string,
    ) => void
    openPaymentsRates: (locationId: string, locationName: string) => void
}

const LocationCardContext = createContext<LocationCardContextValue | null>(null)

export function useLocationCard(): LocationCardContextValue {
    const context = useContext(LocationCardContext)
    if (!context) {
        throw new Error('useLocationCard must be used within a LocationCardProvider')
    }
    return context
}

interface LocationCardProviderProps {
    practiceId: string
    locations: Models.PracticeLocation[]
    hasPayments: boolean
    hasDirectScheduling: boolean
    isLocationEditable: boolean
    canManageSurvey: boolean
    onOpenLocationProductsModal: (locationId: string) => void
    children: React.ReactNode
}

export function LocationCardProvider({
    practiceId,
    locations,
    hasPayments,
    hasDirectScheduling,
    isLocationEditable,
    canManageSurvey,
    onOpenLocationProductsModal,
    children,
}: LocationCardProviderProps): JSX.Element {
    const dispatch = useAppDispatch()

    const [expandedPayments, setExpandedPayments] = useState<ExpandedPaymentsState | null>(null)

    const getSelectedLocation = useCallback(
        (locationId: string): Models.PracticeLocation | undefined => {
            return locations.find(loc => loc.id === locationId)
        },
        [locations],
    )

    const openEditLocation = useCallback(
        async (location: Models.PracticeLocation) => {
            if (hasPayments) {
                await dispatch(fetchLocationStripeAccountId(practiceId, location.id))
                await dispatch(fetchLocationConnectPaymentInfoData(practiceId, location.id))
            }
            dispatch(openLocationAddEditModal(location, practiceId))
        },
        [dispatch, practiceId, hasPayments],
    )

    const openEditSurvey = useCallback(
        (location: Models.PracticeLocation) => {
            dispatch(openEditSurveyModal(location))
        },
        [dispatch],
    )

    const openLocationProducts = useCallback(
        (locationId: string) => {
            onOpenLocationProductsModal(locationId)
        },
        [onOpenLocationProductsModal],
    )

    const togglePaymentsExpand = useCallback(
        (locationId?: string) => {
            if (!locationId) {
                setExpandedPayments(null)
                return
            }

            const isOpening = expandedPayments?.id !== locationId

            setExpandedPayments(
                isOpening
                    ? { id: locationId, isLoading: true, errorMessage: '' }
                    : null,
            )

            if (isOpening) {
                const selected = getSelectedLocation(locationId)
                Promise.all([
                    dispatch(loadLocationPaymentsInfo(practiceId, locationId)),
                    dispatch(fetchLocationStripeAccountId(practiceId, locationId)),
                    dispatch(getLocationPaymentsSettings(practiceId, locationId)),
                ])
                    .then(() => {
                        setExpandedPayments({
                            id: locationId,
                            isLoading: false,
                            errorMessage: '',
                        })
                    })
                    .catch(() => {
                        setExpandedPayments({
                            id: locationId,
                            isLoading: false,
                            errorMessage: `Failed loading payments data for "${selected?.name || locationId}".`,
                        })
                    })
            }
        },
        [dispatch, practiceId, expandedPayments, getSelectedLocation],
    )

    const openTerminal = useCallback(
        (
            locationId: string,
            terminalId: string,
            nickname: string,
            readerSerial: string,
            pairingCode?: string,
            s700Code?: string,
            uuid?: string,
            addTerminalRadio: AddTerminalRadio = 'useSmile',
            isView: boolean = false,
        ) => {
            dispatch(
                openTerminalModal(
                    locationId,
                    practiceId,
                    terminalId,
                    nickname,
                    readerSerial,
                    pairingCode,
                    s700Code,
                    uuid,
                    addTerminalRadio,
                    isView,
                ),
            )
        },
        [dispatch, practiceId],
    )

    const openStripeAccountId = useCallback(
        (
            locationId: string,
            locationName: string,
            stripeAccountId: string,
            applyToAll: boolean,
        ) => {
            dispatch(openStripeAccountIdModal(locationId, locationName, practiceId, stripeAccountId, applyToAll))
        },
        [dispatch, practiceId],
    )

    const openPaymentsDescriptor = useCallback(
        (locationId: string, statementDescriptor: string) => {
            dispatch(openStatementDescriptorModal(locationId, statementDescriptor, practiceId))
        },
        [dispatch, practiceId],
    )

    const openPaymentsRates = useCallback(
        (locationId: string, locationName: string) => {
            dispatch(openPaymentRatesModal(locationId, locationName, practiceId))
        },
        [dispatch, practiceId],
    )

    const value: LocationCardContextValue = {
        practiceId,
        hasPayments,
        hasDirectScheduling,
        isLocationEditable,
        canManageSurvey,
        expandedPayments,
        openEditLocation,
        openEditSurvey,
        openLocationProducts,
        togglePaymentsExpand,
        openTerminal,
        openStripeAccountId,
        openPaymentsDescriptor,
        openPaymentsRates,
    }

    return (
        <LocationCardContext.Provider value={value}>
            {children}
        </LocationCardContext.Provider>
    )
}
